import React from "react";
import { Button, Spin, Tooltip } from "antd";
import {
  LoadingOutlined,
  LoginOutlined,
  SettingOutlined,
} from "@ant-design/icons";

import tokens from "../../styles/tokens";
import { uppercaseFirst } from "../../helpers";
import { DEDICATED_SIZE_COLORS, SERVICE_LEVEL } from "../../constants";

const SIZE_LABELS = {
  XS: "Extra Small",
  S: "Small",
  M: "Medium",
  L: "Large",
};

const SIZE_KEYS = {
  XS: "xsmall",
  S: "small",
  M: "medium",
  L: "large",
};

const REGION_MAP = {
  aws_useast_1: "AWS US East",
};

const SpinnerSmall = <LoadingOutlined style={{ fontSize: 28 }} spin />;

const formatReason = (reason) =>
  uppercaseFirst(reason.replace(/provision/i, "provisioning"));

const getInstanceType = (type) => {
  if (!type) return "";
  if (type.includes("CPU")) return "CPU";
  if (type.includes("GPU")) return "GPU";
  return "";
};

const ACTION_REASON_LABELS = {
  provision_cluster: "cluster provision",
  deprovision_cluster: "cluster deprovision",
  upgrade_cluster: "cluster upgrade",
  backup_cluster: "cluster backup",
  restore_cluster: "cluster restore",
  provision_user: "user provision",
  deprovision_user: "user deprovision",
};

const getProcessingReason = (pendingActions) => {
  if (pendingActions.length === 0) return null;
  return ACTION_REASON_LABELS[pendingActions[0].type] || pendingActions[0].type;
};

const ClusterCard = ({
  cluster,
  assignment,
  actions = [],
  clustersLoading,
  onLogin,
  onManage,
}) => {
  const { service_level, cluster_size, cluster_type, cloud_region } = cluster;
  const isDedicated = service_level === SERVICE_LEVEL.DEDICATED;
  const sizeColor =
    DEDICATED_SIZE_COLORS[SIZE_KEYS[cluster_size]] || tokens.brand[500];

  const status = (cluster.status || "unknown").toLowerCase();
  const pendingActions = actions.filter((a) => a.status === "pending");
  const clusterProcessing = status === "pending";
  const otherProcessing =
    pendingActions.length > 0 ||
    (!clustersLoading && assignment?.status !== "complete");
  const isOnline = !clusterProcessing && status === "online";
  const processingReason = getProcessingReason(pendingActions);

  const title = isDedicated
    ? cluster.cluster_label || "Untitled"
    : "Cloud Free";

  const dotColor = isOnline
    ? tokens.semantic.success
    : clusterProcessing || otherProcessing
    ? tokens.brand[400]
    : tokens.semantic.danger;

  const cardStyle = {
    background: `linear-gradient(160deg, ${tokens.surface[100]} 0%, ${tokens.surface[100]} 35%, ${sizeColor}4d 100%)`,
    borderColor: `${sizeColor}66`,
  };

  const accentStyle = {
    background: `linear-gradient(90deg, transparent 0%, ${sizeColor} 50%, transparent 100%)`,
  };

  const pillStyle = isDedicated
    ? {
        color: tokens.brand[200],
        background: `${tokens.brand[500]}24`,
        borderColor: `${tokens.brand[400]}66`,
      }
    : {
        color: "#dbeafe",
        background: "rgba(96,165,250,0.16)",
        borderColor: "rgba(96,165,250,0.45)",
      };

  return (
    <div className="dashboard-cluster-card" style={cardStyle}>
      <div
        aria-hidden
        className="dashboard-cluster-card__accent"
        style={accentStyle}
      />
      <div
        aria-hidden
        className="dashboard-cluster-card__glow"
        style={{
          background: `radial-gradient(circle, ${sizeColor}59 0%, transparent 70%)`,
        }}
      />

      <div className="dashboard-cluster-card__pill" style={pillStyle}>
        {isDedicated ? "Dedicated" : "Free"}
      </div>

      <div className="dashboard-cluster-card__title-row">
        <span
          className={`status-dot${
            clusterProcessing || otherProcessing ? " status-dot--pulse" : ""
          }`}
          style={{ background: dotColor, boxShadow: `0 0 8px ${dotColor}` }}
        />
        <h3 className="dashboard-cluster-card__title">{title}</h3>
      </div>

      <div className="dashboard-cluster-card__meta">
        <span>{SIZE_LABELS[cluster_size] || cluster_size}</span>
        {getInstanceType(cluster_type) && (
          <>
            <span className="dashboard-cluster-card__meta-sep">·</span>
            <span>{getInstanceType(cluster_type)}</span>
          </>
        )}
        {cloud_region && (
          <>
            <span className="dashboard-cluster-card__meta-sep">·</span>
            <span>{REGION_MAP[cloud_region] || cloud_region}</span>
          </>
        )}
      </div>

      <div className="dashboard-cluster-card__body">
        {clusterProcessing ? (
          <Spin
            indicator={SpinnerSmall}
            spinning
            wrapperClassName="render-processing"
          >
            <div className="dashboard-cluster-card__notice">
              <div className="dashboard-cluster-card__notice-title">
                Provisioning database
              </div>
              <div className="dashboard-cluster-card__notice-body">
                This may take 10–15 minutes. We'll email you when it's ready.
              </div>
            </div>
          </Spin>
        ) : otherProcessing ? (
          <Spin
            indicator={SpinnerSmall}
            spinning
            wrapperClassName="render-processing"
          >
            <div className="dashboard-cluster-card__notice">
              {processingReason
                ? formatReason(processingReason)
                : "Provisioning"}{" "}
              may take a few moments.
            </div>
          </Spin>
        ) : null}
      </div>

      <div className="dashboard-cluster-card__actions">
        <Tooltip
          title={
            assignment?.admin_username ? (
              <div style={{ padding: "4px 6px" }}>
                DB User: <strong>{assignment.admin_username}</strong>
              </div>
            ) : null
          }
          color="magenta"
        >
          <Button
            type="primary"
            size="large"
            onClick={onLogin}
            disabled={assignment?.status !== "complete"}
            className="kinetica-gradient-btn dashboard-cluster-card__login"
            block
          >
            <LoginOutlined /> Login
          </Button>
        </Tooltip>
        <Tooltip title={isOnline ? null : `Status: ${status}`}>
          <Button
            size="large"
            onClick={isOnline ? onManage : undefined}
            disabled={!isOnline}
            className="dashboard-cluster-card__manage"
            block
          >
            <SettingOutlined /> Manage
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

export default ClusterCard;
